// @core/stores/secret/SecretPkiStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { secretApi } from '@core/api';
import type {
  CertificateResponse,
  CreatePkiRoleRequest,
  GenerateIntermediateRequest,
  GenerateIntermediateResponse,
  GenerateRootRequest,
  GetCAResponse,
  GetCRLResponse,
  ListPkiRolesResponse,
  PkiRoleResponse,
  SignIntermediateRequest,
} from '@core/types/secret';

class SecretPkiStore {

  // Loading states
  isLoading = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;

  constructor() {
    makeAutoObservable(this);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }

  reset(): void {
    this.error = null;
  }

  /**
   * Create PKI role
   */
  async CreatePkiRole(engine_id: string, data: CreatePkiRoleRequest): Promise<PkiRoleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.CreatePkiRole(engine_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete PKI role
   */
  async DeletePkiRole(engine_id: string, role_name: string): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.DeletePkiRole(engine_id, role_name);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Generate intermediate CSR
   */
  async GenerateIntermediate(engine_id: string, data: GenerateIntermediateRequest): Promise<GenerateIntermediateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.GenerateIntermediate(engine_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Generate root CA
   */
  async GenerateRoot(engine_id: string, data: GenerateRootRequest): Promise<CertificateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.GenerateRoot(engine_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get CA certificate
   */
  async GetCA(engine_id: string): Promise<GetCAResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.GetCA(engine_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get CRL
   */
  async GetCRL(engine_id: string): Promise<GetCRLResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.GetCRL(engine_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get PKI role
   */
  async GetPkiRole(engine_id: string, role_name: string): Promise<PkiRoleResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.GetPkiRole(engine_id, role_name);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List PKI roles
   */
  async ListPkiRoles(engine_id: string, params?: { limit?: string | number | boolean; cursor?: string | number | boolean; direction?: string | number | boolean; sort?: string | number | boolean; }): Promise<ListPkiRolesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.ListPkiRoles(engine_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Sign intermediate CSR
   */
  async SignIntermediate(engine_id: string, data: SignIntermediateRequest): Promise<CertificateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await secretApi.SignIntermediate(engine_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const secretPkiStore = new SecretPkiStore();
